"use client";

import { ExternalLink, Play, Video } from "lucide-react";
import { useState } from "react";

import { getMediaEmbedPlaybackUrl, getProviderLabel } from "@/lib/mediaEmbed.mjs";

function sourceLabel(embed) {
  const provider = getProviderLabel(embed.provider);
  return embed.authorName ? `${provider} - ${embed.authorName}` : provider;
}

export default function VideoEmbedCard({ embed, preview, className = "" }) {
  const [activated, setActivated] = useState(false);
  const [posterFailed, setPosterFailed] = useState(false);
  const [playerState, setPlayerState] = useState("idle");
  const provider = getProviderLabel(embed.provider);
  const title = embed.title || preview.title || `${provider} video`;
  const canonicalUrl = embed.canonicalUrl || preview.url;
  const playbackUrl = getMediaEmbedPlaybackUrl(embed, { autoplay: true });
  const posterUrl = !posterFailed ? embed.posterUrl || preview.imageUrl || "" : "";
  const aspectRatio = embed.aspectRatio || "16 / 9";
  const isVerticalSocialEmbed = embed.provider === "tiktok";
  const showPoster = !activated || playerState === "loading";

  const activate = () => {
    setActivated(true);
    setPlayerState("loading");
  };

  return (
    <section
      className={`w-full overflow-hidden rounded-[var(--radius)] border border-[var(--border)] bg-[var(--surface-1)] ${isVerticalSocialEmbed ? "mx-auto max-w-sm" : ""} ${className}`}
      aria-label={`${provider} video preview`}
    >
      <div
        className={`relative w-full overflow-hidden ${showPoster && !posterUrl ? "bg-[var(--surface-2)]" : "bg-black"}`}
        style={{ aspectRatio }}
        aria-busy={activated && playerState === "loading"}
      >
        {activated ? (
          embed.provider === "direct-video" ? (
            <video
              controls
              autoPlay
              playsInline
              preload="metadata"
              poster={posterUrl || undefined}
              onCanPlay={() => setPlayerState("ready")}
              onError={() => setPlayerState("error")}
              className="h-full w-full bg-black"
            >
              <source src={playbackUrl} />
              {Array.isArray(embed.captions)
                ? embed.captions.map((track) => (
                    <track
                      key={`${track.src}-${track.srclang}`}
                      kind="captions"
                      src={track.src}
                      srcLang={track.srclang}
                      label={track.label}
                    />
                  ))
                : null}
              Your browser does not support this video format.
            </video>
          ) : (
            <iframe
              title={`${provider} player: ${title}`}
              src={playbackUrl}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              onLoad={() => setPlayerState("ready")}
              className="absolute inset-0 h-full w-full border-0"
              referrerPolicy="strict-origin-when-cross-origin"
              scrolling="no"
            />
          )
        ) : null}

        {showPoster ? (
          <button
            type="button"
            onClick={activated ? undefined : activate}
            disabled={activated}
            className={`group absolute inset-0 flex h-full w-full items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--brand)] disabled:cursor-wait ${posterUrl ? "bg-black text-white" : "bg-[var(--surface-2)] text-[var(--fg)]"}`}
            aria-label={`Play ${provider} video: ${title}`}
          >
            {posterUrl ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={posterUrl}
                alt=""
                onError={() => setPosterFailed(true)}
                className="h-full w-full object-cover opacity-85 transition duration-200 group-hover:opacity-70"
              />
            ) : (
              <span className="flex flex-col items-center gap-3 px-6 text-center">
                <Video aria-hidden="true" className="h-10 w-10 text-[var(--muted)]" />
                <span className="text-sm font-semibold">{provider} video</span>
                <span className="text-xs text-[var(--muted)]">Tap to play</span>
              </span>
            )}
            <span className="absolute inline-flex h-14 w-14 items-center justify-center rounded-full bg-black/75 text-white shadow-lg transition-transform group-hover:scale-105">
              <Play aria-hidden="true" className="ml-1 h-6 w-6 fill-current" />
            </span>
            <span className="sr-only">Play video</span>
          </button>
        ) : null}

        {activated && playerState === "error" ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/85 px-4 text-center text-sm text-white">
            <span>Video playback is unavailable here.</span>
            <a
              href={canonicalUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 rounded-[calc(var(--radius)-4px)] bg-white px-3 py-2 font-semibold text-black focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)]"
            >
              Open on {provider}
              <ExternalLink aria-hidden="true" className="h-4 w-4" />
            </a>
          </div>
        ) : null}
      </div>

      <div className="space-y-1.5 p-3">
        <div className="text-xs font-medium text-[var(--muted)]">{sourceLabel(embed)}</div>
        <a
          href={canonicalUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="line-clamp-2 block text-sm font-semibold text-[var(--fg)] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)]"
        >
          {title}
        </a>
        {preview.description ? (
          <div className="line-clamp-2 text-sm text-[var(--muted)]">{preview.description}</div>
        ) : null}
      </div>
    </section>
  );
}
